import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ActiviteService } from 'src/app/activites/services/activite.service';
import { UrlUtils } from 'src/app/core/helpers/url-utils';
import { Dico } from 'src/app/core/models/dico';
import { EntiteSearchor } from 'src/app/core/models/entite-searchor';
import { IdCodeLabel } from 'src/app/core/models/IdCodeLabel';
import { ProfilDto } from 'src/app/core/models/profil-dto';
import { SearchParam } from 'src/app/core/models/search-param';
import { ControlService } from 'src/app/core/services/control.service';
import { ModalResponse } from 'src/app/core/services/modal-response';
import { ModalResult } from 'src/app/core/services/modal-result';
import { ModalService } from 'src/app/core/services/modal.service';
import { MembreService } from 'src/app/membres/services/membre.service';
import { AssociationMembreActiviteDto } from '../../models/association-membre-activite-dto';

@Component({
  selector: 'app-saisie-membre-activite',
  templateUrl: './saisie-membre-activite.component.html',
  styleUrls: ['./saisie-membre-activite.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SaisieMembreActiviteComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SaisieMembreActiviteComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SaisieMembreActiviteComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @Input() consigne: (ctrl: AbstractControl) => void;
  @Input() readOnly;
  @Input() membreDonne = false;
  @Input() activiteDonnee = false;
  @Input() membreExclusion = [];
  @Input() activiteExclusion = [];
  form: FormGroup;
  membreSearchor: EntiteSearchor<IdCodeLabel>;
  activiteSearchor: EntiteSearchor<IdCodeLabel>;

  constructor(
    private fb: FormBuilder,
    private controlService: ControlService,
    private membreService: MembreService,
    private activiteService: ActiviteService,
    private modalService: ModalService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.form.valueChanges.subscribe((_val) => {
      this.onChange(this.form.getRawValue());
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.form) {
      this.form = this.fb.group({
        id: [null],
        activite: [new IdCodeLabel(), Validators.required],
        membre: [new IdCodeLabel(), Validators.required],
        debutAsso: [null],
        finAsso: [null],
        debutActivite: [null],
        finActivite: [null],
        actPrincipale: [false],
        nfAssociees: [false],
        profils: [[]],
        miseEnSommeilSage: [{ value: false, disabled: true }],
      });
    }

    if (changes['activiteDonnee'] && this.activiteDonnee) {
      this.form.get('actPrincipale').disable();
    }

    if (
      changes['membreExclusion'] &&
      JSON.stringify(changes['membreExclusion'].currentValue) !== JSON.stringify(changes['membreExclusion'].previousValue) &&
      !this.associationId &&
      this.activiteDonnee &&
      this.membreSearchor
    ) {
      this.membreSearchor.addSearch({ idsToExclude: this.membreExclusion });
    }
    if (
      changes['activiteExclusion'] &&
      JSON.stringify(changes['activiteExclusion'].currentValue) !== JSON.stringify(changes['activiteExclusion'].previousValue) &&
      !this.associationId &&
      this.membreDonne &&
      this.activiteSearchor
    ) {
      this.activiteSearchor.searchParam.search = { idsToExclude: this.activiteExclusion };
    }
  }

  validate(_control: AbstractControl): ValidationErrors {
    return this.controlService.validateForm(this.form);
  }

  writeValue(dto: AssociationMembreActiviteDto) {
    if (dto) {
      if (!dto?.id && this.activiteDonnee && !this.membreSearchor) {
        // on crée le membreSearchor
        this.membreSearchor = new EntiteSearchor((searchParam: SearchParam<Dico<any>>) => this.membreService.quickSearch(searchParam), {
          masque: false,
        });
      }
      if (!dto?.id && this.membreDonne && !this.activiteSearchor) {
        // on crée le activiteSearchor
        this.activiteSearchor = new EntiteSearchor((searchParam: SearchParam<Dico<any>>) => this.activiteService.quickSearch(searchParam), {
          masque: false,
        });
      }

      if (dto.activite?.id && !dto?.id) {
        // nouveau membre (critère : on a son id mais pas son label)
        this.activiteSearchor.searchById(dto.activite.id).then((v) => {
          this.form.get('activite').setValue(v);
          this.activiteSearchor.initSearchParam();
        });
        // ligne suivante, pour faire apparaitre la saise d'un nouveau profil
        this.form.markAsTouched();
      }
      if (dto.membre?.id && !dto?.id) {
        // nouvelle activité (critère : on a son id mais pas son label)
        this.form.get('actPrincipale').setValue(true);
        this.membreSearchor.searchById(dto.membre.id).then((v) => {
          this.form.get('membre').setValue(v);
          this.membreSearchor.initSearchParam();
        });
        // ligne suivante, pour faire apparaitre la saise d'un nouveau profil
        this.form.markAsTouched();
      }

      this.form.patchValue(dto, { emitEvent: false });
    } else {
      this.form.reset({ emitEvent: false });
    }
    if (dto.id) {
      this.form.get('activite').disable();
      this.form.get('membre').disable();
    }
    this.form.markAsPristine();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  onTouched: () => void = () => {};
  onChange: (val: AssociationMembreActiviteDto) => void = () => {};

  setDisabledState?(_isDisabled: boolean): void {
    // return isDisabled ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
  }

  get newProfilDto() {
    return new ProfilDto();
  }

  onClickFicheActivite() {
    UrlUtils.onClickEmbbeded(
      {},
      null,
      { embeddedUrl: `/Activites/ActiviteEdit/Edit?activiteId=${this.form.get('activite').value.id}` },
      this.router,
    );
  }

  get currentMembreId() {
    return this.form?.get('membre').value?.id;
  }

  get currentActiviteId() {
    return this.form?.get('activite').value?.id;
  }

  get associationId() {
    return this.form?.get('id').value;
  }

  onMembreChange() {
    this.membreService.getActivitePrincipaleId(this.currentMembreId).subscribe((v) => {
      if (v && v !== this.currentActiviteId) {
        this.form.get('actPrincipale').setValue(false);
      } else {
        this.form.get('actPrincipale').setValue(true);
      }
    });
  }

  onActPrincipaleChange(actPrincipale: any) {
    // On ne peut pas décocher une activité principale, et si on met le controle en disabled
    // le deal ne marche plus, donc bidouille
    // On peut cocher une activité non principale, et le deal s'occupe de mettre toutes les autres en non principale
    if (!actPrincipale.currentTarget.checked) {
      this.form.get('actPrincipale').setValue(true);
    }
  }

  resumeProfil = (dto: ProfilDto) => (dto.profil ? `${dto.profil.text}` : 'nouveau');

  onChangeFinAsso() {
    const modalResponse = new ModalResponse();

    if (this.form.get('profils').value?.length && this.form.valid) {
      // test essentiel sinon le formulaire devient valide après affectaion de la date aux profils,
      // même incomplets(emitEvent à false ds le writeValue de saisie - profil - component)
      // todo : comprendre pourquoi (virer le test sur form.valid)
      modalResponse.onModalReply$.subscribe((modalResult) => {
        if (modalResult === ModalResult.ok) {
          const o = this.form.get('profils').value;
          o.forEach((p) => {
            p.dateFin = this.form.get('finAsso').value;
          });
          this.form.get('profils').setValue(o);
        }
      });
      this.modalService.confirmWarning(
        'Demande de confirmation',
        "Vous modifiez la fin d'association membre / activité. Souhaitez vous affecter cette date de fin aux profils du membre sur cette activité ?",
        'Oui',
        'Non',
        modalResponse,
      );
    }
  }
}
